import { Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import {
  FindingsOrderField,
  FindingType,
  OrderDirection,
  ScoreStateFilter,
  useGetChecksQuery,
} from "~/operations";
import {
  CheckFindingsCheckNode,
  ListPolicyEdge,
} from "~/pages/inventory/asset/types";
import { FailedCheck } from "./FailedCheck";
import { Loading } from "../loading";

const MAX_FINDINGS_PER_PAGE = 100;
type FailedChecksProps = { listPolicyEdge: ListPolicyEdge; assetMrn: string };

export function FailedChecks({ assetMrn, listPolicyEdge }: FailedChecksProps) {
  const { data, loading, fetchMore } = useGetChecksQuery({
    variables: {
      scopeMrn: assetMrn,
      first: MAX_FINDINGS_PER_PAGE,
      orderBy: {
        field: FindingsOrderField.RiskValue,
        direction: OrderDirection.Desc,
      },
      filter: {
        policyMrn: listPolicyEdge.node.mrn,
        state: ScoreStateFilter.Open,
        types: [FindingType.Check],
      },
    },
  });

  // Keep fetching until hasNextPage is false
  useEffect(() => {
    if (!data || data.findings?.__typename !== "FindingsConnection") return;
    let { hasNextPage, endCursor } = data.findings.pageInfo;
    if (!hasNextPage) return;
    const controller = new AbortController();
    (async () => {
      try {
        while (!controller.signal.aborted && hasNextPage) {
          const res = await fetchMore({
            variables: { after: endCursor, first: MAX_FINDINGS_PER_PAGE },
            context: { fetchOptions: { signal: controller.signal } },
          });
          if (res.data?.findings?.__typename === "FindingsConnection") {
            hasNextPage = res.data.findings.pageInfo.hasNextPage;
            endCursor = res.data.findings.pageInfo.endCursor;
          } else {
            hasNextPage = false;
          }
        }
      } catch (error) {
        console.error("Error fetching more checks:", error);
        hasNextPage = false;
      }
    })();
    return () => {
      controller.abort();
    };
  }, [data, fetchMore]);

  if (loading || !data) {
    return (
      <Grid
        sx={{
          py: 4,
          borderBottom: "1px solid #DBDADD",
        }}
        size={12}
      >
        <Loading what="failed checks" />
      </Grid>
    );
  }

  const findingsUnion =
    data?.findings?.__typename === "FindingsConnection"
      ? data.findings
      : undefined;
  const checkEdges = findingsUnion?.edges || [];
  const checks: CheckFindingsCheckNode[] = checkEdges.flatMap((e) =>
    e?.node?.__typename === "CheckFinding" ? e.node : [],
  );

  return (
    <Grid
      container
      sx={{
        py: 4,
        borderBottom: "1px solid #DBDADD",
      }}
      size={12}
    >
      <Typography variant="h5" component="h4" sx={{ px: 4 }}>
        Failed Checks
      </Typography>
      <Grid
        container
        sx={{
          "& .failed-check": {
            borderBottom: "1px solid #DBDADD",
          },
          "& .failed-check:last-child": {
            borderBottom: "none",
          },
        }}
      >
        {checks?.map((edge) => {
          const check = edge;
          return (
            <FailedCheck key={check?.mrn} assetMrn={assetMrn} check={check} />
          );
        })}
      </Grid>
    </Grid>
  );
}
